import React from 'react';
import { Link } from 'react-router-dom';
import { Gauge, Calendar, Car } from 'lucide-react';

const VehicleCard = ({ vehicle }) => {
  const defaultImage = 'https://images.unsplash.com/photo-1494976388531-d1058494cdd8?w=400&h=300&fit=crop';
  const imageUrl = vehicle.images && vehicle.images.length > 0 ? vehicle.images[0] : defaultImage;

  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatMileage = (mileage) => {
    return new Intl.NumberFormat('en-US').format(mileage);
  };

  // Get display drivetrain
  const getDrivetrain = () => {
    if (vehicle.drivetrain) {
      if (vehicle.drivetrain === '4WD' || vehicle.drivetrain === 'AWD') {
        return vehicle.drivetrain;
      }
      return vehicle.drivetrain;
    }
    return null;
  };

  // Feature badges to display (limit to 3 for space)
  const getFeatureBadges = () => {
    const badges = [];
    const drivetrain = getDrivetrain();
    
    if (drivetrain === '4WD' || drivetrain === 'AWD') {
      badges.push(drivetrain);
    }
    
    const priorityFeatures = ['Backup Camera', 'Apple CarPlay', 'Android Auto', 'Bluetooth Radio'];
    for (const feature of priorityFeatures) {
      if (vehicle.features?.includes(feature) && badges.length < 3) {
        badges.push(feature);
      }
    }
    
    return badges;
  };

  const featureBadges = getFeatureBadges();

  return (
    <Link
      to={`/inventory/${vehicle.stock_number || vehicle.id}`}
      className="car-card bg-[#132238] rounded-xl overflow-hidden border border-[#1a2d47] block hover:border-[#1a4d8c] transition-all w-full"
      data-testid={`vehicle-card-${vehicle.id}`}
    >
      {/* Image */}
      <div className="relative h-48 overflow-hidden">
        <img
          src={imageUrl}
          alt={`${vehicle.year} ${vehicle.make} ${vehicle.model}`}
          className="w-full h-full object-cover"
          onError={(e) => { e.target.src = defaultImage; }}
        />
        {vehicle.is_featured && (
          <span className="badge-featured absolute top-3 left-3">Featured</span>
        )}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4">
          <p className="price-tag text-2xl font-bold text-white">{formatPrice(vehicle.price)}</p>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        {/* Vehicle Title - Year Make Model on top */}
        <h3 className="text-lg font-bold text-white mb-1">
          {vehicle.year} {vehicle.make} {vehicle.model}
        </h3>
        {vehicle.trim && <p className="text-[#a8b8d0] text-sm mb-3">{vehicle.trim}</p>}

        {/* Key Specs - Compact row */}
        <div className="flex items-center gap-4 text-sm text-[#a8b8d0] mb-3">
          <div className="flex items-center gap-1">
            <Gauge size={14} className="text-[#ff6b35]" />
            <span>{formatMileage(vehicle.mileage)} mi</span>
          </div>
          {getDrivetrain() && (
            <div className="flex items-center gap-1">
              <Car size={14} className="text-[#ff6b35]" />
              <span>{getDrivetrain()}</span>
            </div>
          )}
          <div className="flex items-center gap-1">
            <Calendar size={14} className="text-[#ff6b35]" />
            <span>{vehicle.year}</span>
          </div>
        </div>

        {/* Feature Badges */}
        {featureBadges.length > 0 && (
          <div className="flex flex-wrap gap-1.5 mb-3">
            {featureBadges.map((badge, index) => (
              <span 
                key={index}
                className="text-xs px-2 py-1 rounded-full bg-[#1a4d8c]/50 text-[#a8b8d0] border border-[#1a4d8c]"
              >
                {badge}
              </span>
            ))}
          </div>
        )}

        {/* CTA */}
        <div className="pt-3 border-t border-[#1a2d47]">
          <span className="text-[#ff6b35] font-medium text-sm hover:text-[#ffc107] transition-colors">
            View Details →
          </span>
        </div>
      </div>
    </Link>
  );
};

export default VehicleCard;
